import { useAuth } from "@/lib/store";
import { hasPermission } from "@/lib/permissions";
import { billingAccessCopy } from "@/lib/billingAccessCopy";
import { useEffect, useState } from "react";
import { ArrowRight, Eye, LockKeyhole } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { setSubscriptionReadOnlyHandler } from "@/lib/apiClient";
import { useLocalization } from "@/lib/localization";

const COPY = {
  en: { badge: "Read-only mode", title: "Your records are safe", body: "Your subscription has ended, so changes are paused for now.", viewTitle: "You can still view everything", viewBody: "Renew whenever you’re ready to create, edit, or delete records again.", action: "View subscription", close: "Continue viewing" },
  fr: { badge: "Mode lecture seule", title: "Vos données sont en sécurité", body: "Votre abonnement est terminé. Les modifications sont temporairement suspendues.", viewTitle: "Vous pouvez toujours tout consulter", viewBody: "Renouvelez pour créer, modifier ou supprimer des données.", action: "Voir l’abonnement", close: "Continuer à consulter" },
  ar: { badge: "وضع القراءة فقط", title: "سجلاتك آمنة", body: "انتهى اشتراكك، لذلك تم إيقاف التغييرات مؤقتاً.", viewTitle: "لا يزال بإمكانك عرض كل شيء", viewBody: "جدّد اشتراكك عندما تكون مستعداً لإنشاء السجلات أو تعديلها أو حذفها.", action: "عرض الاشتراك", close: "متابعة العرض" },
  ha: { badge: "Yanayin karantawa kawai", title: "Bayanan ka suna lafiya", body: "Biyan kuɗinka ya ƙare, don haka an dakatar da canje-canje.", viewTitle: "Har yanzu kana iya ganin komai", viewBody: "Sabunta domin ƙirƙira, gyara ko goge bayanai.", action: "Duba biyan kuɗi", close: "Ci gaba da dubawa" },
  ig: { badge: "Nlele naanị", title: "Ndekọ gị dị nchebe", body: "Ndebanye aha gị agwụla, ya mere a kwụsịla mgbanwe.", viewTitle: "Ị ka nwere ike ịhụ ihe niile", viewBody: "Megharịa iji mepụta, dezie ma ọ bụ hichapụ ndekọ.", action: "Lee ndebanye aha", close: "Gaa n’ihu ilele" },
  pt: { badge: "Modo só de leitura", title: "Os seus registos estão seguros", body: "A subscrição terminou, por isso as alterações estão suspensas.", viewTitle: "Ainda pode consultar tudo", viewBody: "Renove para criar, editar ou eliminar registos.", action: "Ver subscrição", close: "Continuar a consultar" },
  sw: { badge: "Hali ya kusoma tu", title: "Rekodi zako ziko salama", body: "Usajili wako umeisha, kwa hiyo mabadiliko yamesitishwa.", viewTitle: "Bado unaweza kuona kila kitu", viewBody: "Sasisha ili kuunda, kuhariri au kufuta rekodi.", action: "Angalia usajili", close: "Endelea kutazama" },
  yo: { badge: "Kíkà nìkan", title: "Àwọn àkọsílẹ̀ rẹ wà láàbò", body: "Alabapin rẹ ti parí, nítorí náà a dá àwọn àyípadà dúró.", viewTitle: "O ṣì lè wo gbogbo nǹkan", viewBody: "Túnṣe láti ṣẹ̀dá, ṣàtúnṣe tàbí pa àkọsílẹ̀ rẹ́.", action: "Wo alabapin", close: "Tẹ̀síwájú wíwo" },
  zu: { badge: "Ukufunda kuphela", title: "Amarekhodi akho aphephile", body: "Ukubhalisa kuphelile, ngakho izinguquko zimisiwe.", viewTitle: "Usengabuka konke", viewBody: "Vuselela ukuze wakhe, uhlele noma ususe amarekhodi.", action: "Buka ukubhalisa", close: "Qhubeka ubuka" },
};

export function SubscriptionReadOnlyDialog() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const canManageBilling = hasPermission(session, "billing");
  const { language } = useLocalization();
  const copy = COPY[language] || COPY.en;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setSubscriptionReadOnlyHandler(() => {
      toast.dismiss();
      setOpen(true);
      window.setTimeout(() => toast.dismiss(), 0);
    });
    return () => setSubscriptionReadOnlyHandler(null);
  }, []);

  const viewSubscription = () => {
    setOpen(false);
    if (canManageBilling) navigate("/subscription");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-[450px] overflow-hidden border-0 bg-card p-0 shadow-[0_32px_100px_-34px_rgba(6,78,59,0.72)] sm:rounded-[30px]">
        <div className="relative overflow-hidden bg-gradient-to-br from-emerald-950 via-primary to-emerald-700 px-7 pb-8 pt-7 text-white">
          <div className="absolute -right-14 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="absolute -bottom-20 -left-12 h-40 w-40 rounded-full bg-emerald-300/10" />
          <div className="relative flex flex-col items-center text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-[20px] border border-white/20 bg-white/15 shadow-lg">
              <LockKeyhole className="h-6 w-6" />
            </div>
            <div className="mt-5 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/85">
              {copy.badge}
            </div>
            <DialogTitle className="mt-4 text-[28px] font-semibold leading-tight tracking-tight text-white">
              {copy.title}
            </DialogTitle>
            <DialogDescription className="mt-3 max-w-sm text-sm leading-6 text-white/80">
              {canManageBilling ? copy.body : billingAccessCopy[language].message}
            </DialogDescription>
          </div>
        </div>

        <div className="space-y-5 px-7 pb-7 pt-6">
          <div className="flex items-start gap-4 rounded-2xl border border-primary/15 bg-primary/5 p-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Eye className="h-5 w-5" />
            </div>
            <div>
              <div className="font-semibold text-foreground">{copy.viewTitle}</div>
              <p className="mt-1 text-sm leading-5 text-muted-foreground">
                {copy.viewBody}
              </p>
            </div>
          </div>

          {canManageBilling ? (
            <Button className="h-12 w-full rounded-xl text-sm font-semibold" onClick={viewSubscription}>
              {copy.action}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : null}
          <button
            className="w-full text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => setOpen(false)}
            type="button"
          >
            {copy.close}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
